"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 8,
  order: "desc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-2 gap-x-6 gap-y-12 md:grid-cols-3 lg:grid-cols-4">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="space-y-6">
            <div className="aspect-[3/4] bg-muted/30 animate-pulse" />
            <div className="space-y-2">
              <div className="h-3 w-16 bg-muted/30 animate-pulse" />
              <div className="h-5 w-3/4 bg-muted/30 animate-pulse" />
              <div className="h-4 w-12 bg-muted/30 animate-pulse" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground py-12 text-center">
        Unable to load collection.{" "}
        <Button onClick={refetch} variant="link" className="p-0 h-auto underline">
          Retry
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="py-12 text-center">
        <p className="text-muted-foreground font-default text-xl italic">No items available in this collection.</p>
      </div>;
  }
  return <div className="grid grid-cols-2 gap-x-6 gap-y-12 md:grid-cols-3 lg:grid-cols-4">
      {products.map((product, i) => {
      const category = product.categories && product.categories.length > 0 ? product.categories[0].name : "Collection";
      return <article key={product.id} data-index={i} className="group flex flex-col">
            <Link to={product.link} className="block">
              <div className="relative overflow-hidden bg-card aspect-[3/4]">
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]" /> : <div className="h-full w-full bg-muted/20" />}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  <span className="border border-foreground bg-background/90 px-6 py-3 text-[11px] uppercase tracking-[0.2em] text-foreground font-default">
                    View Details
                  </span>
                </div>
              </div>
            </Link>

            <div className="mt-6 flex flex-col gap-2">
              <span className="text-[11px] uppercase tracking-[0.2em] font-default text-muted-foreground">
                {category}
              </span>
              <Link to={product.link} className="block">
                <h3 className="font-default text-lg leading-tight text-foreground transition-colors duration-300 group-hover:text-accent">
                  {product.title}
                </h3>
              </Link>
              <div className="font-mono text-sm text-muted-foreground transition-colors duration-300 group-hover:text-foreground">
                ${typeof product.price === "number" ? product.price.toFixed(2) : product.price}
              </div>
              <div className="mt-3">
                <AddToCartButton productId={product.id} variant="link" className="w-full" buttonClassName="bg-foreground text-background hover:bg-accent hover:text-foreground" text="Add to cart" showQuantity={false} />
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function CategoryGallery() {
  return <section data-section-id="4254" id="category-gallery" className="bg-background text-foreground py-24 md:py-40">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="h-px w-full bg-border" />

        <div className="grid grid-cols-1 gap-10 pt-12 md:grid-cols-2 md:gap-16 md:pt-16">
          <div className="flex flex-col">
            <span className="text-[0.75rem] uppercase tracking-[0.2em] font-mono text-muted-foreground">
              § 01 — The Edit
            </span>
            <h2 className="mt-6 font-default text-4xl font-bold leading-[0.9] text-foreground md:text-6xl">
              Curated Essentials.
            </h2>
          </div>

          <div className="flex flex-col justify-between gap-8 md:items-end md:text-right">
            <p className="font-default text-base leading-relaxed text-muted-foreground md:max-w-md">
              Discover our latest seasonal arrivals, meticulously selected for the modern wardrobe. High-street aesthetics meet editorial precision.
            </p>
            <Link to="/ShopPage" className="group inline-flex items-center gap-3 self-start text-[11px] uppercase tracking-[0.2em] text-foreground transition-colors duration-300 hover:text-accent md:self-end font-default">
              <span className="border-b border-foreground/40 pb-1 transition-colors duration-300 group-hover:border-accent">
                Explore Collection
              </span>
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <div className="mt-20 md:mt-24">
          <EcommerceProductsProvider wp_query={{
          per_page: 8,
          order: "desc",
          orderby: "date"
        }}>
            <ProductsGridConsumer />
          </EcommerceProductsProvider>
        </div>

        <div className="mt-16 flex justify-center md:hidden">
          <Link to="/ShopPage">
            <Button variant="outline" className="border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background px-8 py-6 text-[11px] uppercase tracking-[0.2em]">
              View All
              <ArrowRight className="ml-2 h-3.5 w-3.5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}